import { lazy, Suspense, type ReactNode, type SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  type ColorProTradingSuiteV31StrategyActiveTrade,
  type ColorProTradingSuiteV31StrategyStats,
  type ColorProTradingSuiteV31StrategyTrade,
} from '../overlays/colorProTradingSuiteV31Strategy';
import {
  clearBotTrades,
  resetBotSession,
  type BotTradeEventPayload,
} from '../api/botMonitor';
import type {
  OpenCode5mStrategyActiveTrade,
  OpenCode5mStrategyStats,
  OpenCode5mStrategyTrade,
} from '../overlays/openCode5mStrategy';
import type { StrategyStats, StrategyTrade } from '../overlays/traderPro17Strategy';
import type {
  CandleInterval,
  BasicIndicatorKey,
  BasicStudyMaCrossLineType,
  BollingerBandsBasisType,
  BollingerBandsSource,
  OverlayLineStyle,
  OverlaySettings,
  OverlaySettingsTab,
  OverlaySettingsTarget,
  PivotPointsLabelPosition,
  PivotPointsLevel,
  PivotPointsTimeframe,
  PivotPointsType,
} from '../types';
import { BASIC_INDICATOR_DEFINITIONS, isBasicIndicatorKey } from '../indicatorRegistry';
import {
  DEFAULT_PIVOT_LEVELS_COLOR,
  DEFAULT_PIVOT_LEVELS_VISIBILITY,
} from '../overlays/basicStudies';
import { useKeyboardSafeModal } from '../hooks/useKeyboardSafeModal';
import { StrategyFeeRows } from './strategyInfo/shared';
import { ConfirmDialog } from './ConfirmDialog';

const OverlaySettingsSidePanels = lazy(async () => {
  const module = await import('./overlaySettings/OverlaySettingsSidePanels');
  return { default: module.OverlaySettingsSidePanels };
});

const StrategySettingsPanels = lazy(async () => {
  const module = await import('./overlaySettings/StrategySettingsPanels');
  return { default: module.StrategySettingsPanels };
});

const BasicIndicatorSettingsPanels = lazy(async () => {
  const module = await import('./overlaySettings/BasicIndicatorSettingsPanels');
  return { default: module.BasicIndicatorSettingsPanels };
});

const ColorProV31StrategySettingsPanels = lazy(async () => {
  const module = await import('./overlaySettings/ColorProV31StrategySettingsPanels');
  return { default: module.ColorProV31StrategySettingsPanels };
});

const BotTradesSettingsPanels = lazy(async () => {
  const module = await import('./overlaySettings/BotTradesSettingsPanels');
  return { default: module.BotTradesSettingsPanels };
});

type BotConfirmAction = 'clearHistory' | 'resetSession' | null;

interface OverlaySettingsModalProps {
  openTarget: OverlaySettingsTarget | null;
  settings: OverlaySettings;
  defaultSettings: OverlaySettings;
  botTradeEvents?: BotTradeEventPayload[];
  currentSymbol?: string;
  onRefreshBotTrades?: () => Promise<void>;
  strategyStats?: StrategyStats | null;
  strategyTrades?: StrategyTrade[];
  isStrategyAdded?: boolean;
  isStrategyTimeframeSelected?: boolean;
  strategyTimeframe?: CandleInterval;
  isStrategyMinuteRefinementEnabled?: boolean;
  isStrategyMinuteSnapshotLoading?: boolean;
  strategyMinuteDataErrorMessage?: string | null;
  isStrategyMinuteRefinementLoading?: boolean;
  isStrategyMinuteRetryScheduled?: boolean;
  isStrategyMinuteCoverageStalled?: boolean;
  isStrategyMinuteRefinementReady?: boolean;
  missingStrategyMinuteBuckets?: number;
  strategyRefreshDateErrorMessage?: string | null;
  strategyRefreshDateInfoMessage?: string | null;
  strategyRefreshWindowLabel?: string | null;
  strategyRefreshNeedsManualApply?: boolean;
  canRefreshStrategyData?: boolean;
  isRefreshingStrategyData?: boolean;
  canExportStrategy?: boolean;
  onExportStrategy?: () => void;
  onRefreshStrategyData?: () => void;
  colorProV31StrategyStats?: ColorProTradingSuiteV31StrategyStats | null;
  colorProV31StrategyTrades?: ColorProTradingSuiteV31StrategyTrade[];
  colorProV31StrategyActiveTrade?: ColorProTradingSuiteV31StrategyActiveTrade | null;
  isColorProV31StrategyAdded?: boolean;
  isColorProV31StrategyTimeframeSelected?: boolean;
  colorProV31StrategyTimeframe?: CandleInterval;
  openCode5mStrategyStats?: OpenCode5mStrategyStats | null;
  openCode5mStrategyTrades?: OpenCode5mStrategyTrade[];
  openCode5mStrategyActiveTrade?: OpenCode5mStrategyActiveTrade | null;
  isOpenCode5mStrategyAdded?: boolean;
  isOpenCode5mStrategyTimeframeSelected?: boolean;
  openCode5mStrategyTimeframe?: CandleInterval;
  onRefreshColorProV31StrategyData?: () => void;
  isRefreshingColorProV31StrategyData?: boolean;
  canRefreshColorProV31StrategyData?: boolean;
  colorProV31RefreshWindowLabel?: string | null;
  canExportColorProV31Strategy?: boolean;
  onExportColorProV31Strategy?: () => void;
  onClose: () => void;
  onApply: (nextSettings: OverlaySettings) => void;
}

interface RowProps {
  label: string;
  children: ReactNode;
}

const TABS: OverlaySettingsTab[] = ['inputs', 'style', 'visibility'];
const LINE_STYLE_OPTIONS: OverlayLineStyle[] = ['solid', 'dashed', 'dotted'];
const BOLLINGER_SOURCE_OPTIONS: BollingerBandsSource[] = [
  'close',
  'open',
  'high',
  'low',
  'hl2',
  'hlc3',
  'ohlc4',
];
const BOLLINGER_BASIS_TYPE_OPTIONS: BollingerBandsBasisType[] = ['sma', 'ema', 'smma', 'wma', 'vwma'];
const BASIC_STUDY_MA_CROSS_LINE_TYPE_OPTIONS: BasicStudyMaCrossLineType[] = ['ma', 'sma', 'ema'];
const PIVOT_POINTS_TYPE_OPTIONS: PivotPointsType[] = [
  'traditional',
  'fibonacci',
  'woodie',
  'classic',
  'dm',
  'camarilla',
];
const PIVOT_POINTS_TIMEFRAME_OPTIONS: PivotPointsTimeframe[] = [
  'auto',
  'daily',
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
];
const PIVOT_POINTS_LABEL_POSITION_OPTIONS: PivotPointsLabelPosition[] = ['left', 'right'];
const PIVOT_POINTS_LEVELS: PivotPointsLevel[] = ['p', 'r1', 'r2', 'r3', 'r4', 'r5', 's1', 's2', 's3', 's4', 's5'];
const MOVING_AVERAGE_TYPE_OPTIONS: OverlaySettings['movingAverage']['inputs']['maType'][] = [
  'ma',
  'ema',
  'sma',
];
const MOVING_AVERAGE_SOURCE_OPTIONS: OverlaySettings['movingAverage']['inputs']['source'][] = [
  'close',
  'open',
  'high',
  'low',
];
const MURREY_FRAME_MULTIPLIER_OPTIONS: Array<1 | 1.5 | 2> = [1, 1.5, 2];
const MURREY_SOURCE_MODE_OPTIONS: Array<'highLow' | 'openClose'> = ['highLow', 'openClose'];
const MURREY_EXTRA_LINES_COUNT_OPTIONS: Array<2 | 4 | 6> = [2, 4, 6];
const MURREY_ALTERNATIVE_TIMEFRAME_OPTIONS: CandleInterval[] = [
  '1m',
  '3m',
  '5m',
  '15m',
  '30m',
  '45m',
  '1h',
  '2h',
  '3h',
  '4h',
  '1d',
  '1w',
  '1M',
];

function cloneSettings<T>(settings: T): T {
  return JSON.parse(JSON.stringify(settings)) as T;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function pivotPointsLevelLabel(level: PivotPointsLevel): string {
  return level === 'p' ? 'P' : level.toUpperCase();
}

function toDateInputValue(value: string): string {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (!match) {
    return '';
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const parsedMs = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  const parsedDate = new Date(parsedMs);
  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return '';
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function fromDateInputValue(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return '';
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsedMs = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
  const parsedDate = new Date(parsedMs);
  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return '';
  }

  return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${String(year).padStart(4, '0')}`;
}

function titleForTarget(target: OverlaySettingsTarget): string {
  if (isBasicIndicatorKey(target)) {
    return BASIC_INDICATOR_DEFINITIONS[target].label;
  }

  if (target === 'volume') {
    return 'Volume';
  }
  if (target === 'rsi') {
    return 'RSI';
  }
  if (target === 'macd') {
    return 'MACD';
  }
  if (target === 'cmVixFixUcsSqueeze') {
    return 'CM VixFix + Squeeze';
  }
  if (target === 'movingAverage') {
    return 'Moving Average';
  }
  if (target === 'murreyMathLinesV6') {
    return 'Murrey Math Lines v6';
  }
  if (target === 'bollingerBands') {
    return 'Bollinger Bands';
  }
  if (target === 'ichimokuCloud') {
    return 'Ichimoku Cloud';
  }
  if (target === 'candleHelper') {
    return 'Candle Helper';
  }
  if (target === 'traderProV3') {
    return 'Trader Pro v3';
  }
  if (target === 'traderPro17') {
    return 'Trader Pro 1.7';
  }
  if (target === 'traderPro17Strategy') {
    return 'Trader Pro 1.7 Strategy';
  }
  if (target === 'openCode5mStrategy') {
    return 'OpenCode 5m Strategy';
  }
  if (target === 'botTrades') {
    return 'Bot Trades';
  }
  if (target === 'colorProTradingSuiteV31') {
    return 'ColorPro v3.1';
  }
  if (target === 'colorProTradingSuiteV31Strategy') {
    return 'ColorPro v3.1 Strategy';
  }
  return 'ColorPro v3';
}

function asBasicIndicatorKey(target: OverlaySettingsTarget): BasicIndicatorKey {
  return target as BasicIndicatorKey;
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden>
      <path
        d="M3.5 3.5l9 9m0-9l-9 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SettingRow({ label, children }: RowProps) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-md border border-slate-700/80 bg-slate-950/35 px-3 py-2 text-sm text-slate-100">
      <span className="min-w-0 flex-1 text-slate-200">{label}</span>
      <span className="flex shrink-0 items-center gap-2">{children}</span>
    </label>
  );
}

export function OverlaySettingsModal({
  openTarget,
  settings,
  defaultSettings,
  botTradeEvents = [],
  currentSymbol = 'XBTUSD',
  onRefreshBotTrades,
  strategyStats = null,
  strategyTrades = [],
  isStrategyAdded = false,
  isStrategyTimeframeSelected = false,
  strategyTimeframe = '5m',
  isStrategyMinuteRefinementEnabled = false,
  isStrategyMinuteSnapshotLoading = false,
  strategyMinuteDataErrorMessage = null,
  isStrategyMinuteRefinementLoading = false,
  isStrategyMinuteRetryScheduled = false,
  isStrategyMinuteCoverageStalled = false,
  isStrategyMinuteRefinementReady = true,
  missingStrategyMinuteBuckets = 0,
  strategyRefreshDateErrorMessage = null,
  strategyRefreshDateInfoMessage = null,
  strategyRefreshWindowLabel = null,
  strategyRefreshNeedsManualApply = false,
  canRefreshStrategyData = true,
  isRefreshingStrategyData = false,
  canExportStrategy = false,
  onExportStrategy,
  onRefreshStrategyData,
  colorProV31StrategyStats = null,
  colorProV31StrategyTrades = [],
  colorProV31StrategyActiveTrade = null,
  isColorProV31StrategyAdded = false,
  isColorProV31StrategyTimeframeSelected = false,
  colorProV31StrategyTimeframe = '1h',
  openCode5mStrategyStats = null,
  openCode5mStrategyTrades = [],
  openCode5mStrategyActiveTrade = null,
  isOpenCode5mStrategyAdded = false,
  isOpenCode5mStrategyTimeframeSelected = false,
  openCode5mStrategyTimeframe = '5m',
  onRefreshColorProV31StrategyData,
  isRefreshingColorProV31StrategyData = false,
  canRefreshColorProV31StrategyData = true,
  colorProV31RefreshWindowLabel = null,
  canExportColorProV31Strategy = false,
  onExportColorProV31Strategy,
  onClose,
  onApply,
}: OverlaySettingsModalProps) {
  const [activeTab, setActiveTab] = useState<OverlaySettingsTab>('inputs');
  const [draftSettings, setDraftSettingsState] = useState<OverlaySettings>(() => cloneSettings(settings));
  const [initialSettings, setInitialSettings] = useState<OverlaySettings>(() => cloneSettings(settings));
  const [isClearingBotHistory, setIsClearingBotHistory] = useState(false);
  const [isResettingBotSession, setIsResettingBotSession] = useState(false);
  const [botHistoryMessage, setBotHistoryMessage] = useState<string | null>(null);
  const [botHistoryError, setBotHistoryError] = useState<string | null>(null);
  const [botConfirmAction, setBotConfirmAction] = useState<BotConfirmAction>(null);
  const { dialogRef, isKeyboardSafeLayout, viewportStyle } = useKeyboardSafeModal(openTarget !== null);

  const setDraftSettings = (nextSettings: SetStateAction<OverlaySettings>) => {
    setDraftSettingsState((previousSettings) => {
      const resolvedSettings =
        typeof nextSettings === 'function'
          ? (nextSettings as (prevSettings: OverlaySettings) => OverlaySettings)(previousSettings)
          : nextSettings;

      onApply(resolvedSettings);
      return resolvedSettings;
    });
  };

  useEffect(() => {
    if (!openTarget) {
      return;
    }

    const snapshot = cloneSettings(settings);
    setInitialSettings(snapshot);
    setDraftSettingsState(cloneSettings(snapshot));
    setActiveTab('inputs');
  }, [openTarget]);

  useEffect(() => {
    if (openTarget !== 'botTrades') {
      setIsClearingBotHistory(false);
      setIsResettingBotSession(false);
      setBotHistoryMessage(null);
      setBotHistoryError(null);
      setBotConfirmAction(null);
      return;
    }

    setBotHistoryMessage(null);
    setBotHistoryError(null);
  }, [openTarget, currentSymbol]);

  useEffect(() => {
    if (!openTarget || botConfirmAction !== null) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [botConfirmAction, openTarget, onClose]);

  const title = useMemo(() => (openTarget ? titleForTarget(openTarget) : ''), [openTarget]);

  const executeClearBotHistory = async (): Promise<void> => {
    setIsClearingBotHistory(true);
    setBotHistoryMessage(null);
    setBotHistoryError(null);

    try {
      const response = await clearBotTrades({ symbol: currentSymbol });
      await onRefreshBotTrades?.();
      setBotHistoryMessage(`Cleared ${response.deleted} event${response.deleted === 1 ? '' : 's'} for ${currentSymbol}.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to clear bot history.';
      setBotHistoryError(message);
    } finally {
      setIsClearingBotHistory(false);
    }
  };

  const executeResetBotSession = async (): Promise<void> => {
    setIsResettingBotSession(true);
    setBotHistoryMessage(null);
    setBotHistoryError(null);

    try {
      const response = await resetBotSession({ symbol: currentSymbol });
      await onRefreshBotTrades?.();
      setBotHistoryMessage(
        `Session reset for ${response.symbol}: cleared ${response.deletedTrades} trade events, removed ${response.deletedStatuses} status rows, deleted ${response.deletedStateFiles} state file${response.deletedStateFiles === 1 ? '' : 's'}. Restart the bot now.`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reset bot session.';
      setBotHistoryError(message);
    } finally {
      setIsResettingBotSession(false);
    }
  };

  const handleCloseBotConfirm = (): void => {
    setBotConfirmAction(null);
  };

  const handleConfirmBotAction = async (): Promise<void> => {
    if (botConfirmAction === 'clearHistory') {
      setBotConfirmAction(null);
      await executeClearBotHistory();
      return;
    }

    if (botConfirmAction === 'resetSession') {
      setBotConfirmAction(null);
      await executeResetBotSession();
    }
  };

  const botConfirmConfig =
    botConfirmAction === 'clearHistory'
      ? {
          title: 'Clear bot history',
          message: `Clear bot trade history for current chart symbol (${currentSymbol})?`,
          confirmLabel: 'Clear History',
          confirmTone: 'danger' as const,
        }
      : botConfirmAction === 'resetSession'
        ? {
            title: 'Reset bot session',
            message:
              `Reset bot session for ${currentSymbol}? This clears chart history for this symbol, removes local bot session state, and requires restarting the bot process.`,
            confirmLabel: 'Reset Session',
            confirmTone: 'warning' as const,
          }
        : null;

  if (!openTarget) {
    return null;
  }

  const isStrategyTarget = openTarget === 'traderPro17Strategy';
  const isColorProV31StrategyTarget = openTarget === 'colorProTradingSuiteV31Strategy';
  const isOpenCode5mStrategyTarget = openTarget === 'openCode5mStrategy';
  const isBotTradesTarget = openTarget === 'botTrades';
  const colorProSettingsTarget =
    openTarget === 'colorProTradingSuiteV31' ? 'colorProTradingSuiteV31' : 'colorProTradingSuiteV3';
  const colorProSettings = draftSettings[colorProSettingsTarget];
  const setColorProSettings = (
    updater: (
      settings: OverlaySettings['colorProTradingSuiteV3'] | OverlaySettings['colorProTradingSuiteV31'],
    ) => OverlaySettings['colorProTradingSuiteV3'] | OverlaySettings['colorProTradingSuiteV31'],
  ) => {
    setDraftSettings((prev) => {
      if (colorProSettingsTarget === 'colorProTradingSuiteV31') {
        return {
          ...prev,
          colorProTradingSuiteV31: updater(
            prev.colorProTradingSuiteV31,
          ) as OverlaySettings['colorProTradingSuiteV31'],
        };
      }

      return {
        ...prev,
        colorProTradingSuiteV3: updater(prev.colorProTradingSuiteV3) as OverlaySettings['colorProTradingSuiteV3'],
      };
    });
  };
  const hasSidePanel =
    isStrategyTarget || isColorProV31StrategyTarget || isOpenCode5mStrategyTarget || isBotTradesTarget;
  const trailingWins = strategyTrades.filter(
    (trade) => trade.exitReason === 'trailingStop' && trade.pnlPercent !== null && trade.pnlPercent > 0,
  ).length;
  const trailingLosses = strategyTrades.filter(
    (trade) => trade.exitReason === 'trailingStop' && trade.pnlPercent !== null && trade.pnlPercent <= 0,
  ).length;
  const refinedExitCount = strategyTrades.filter(
    (trade) => trade.executionResolution === '1m-refined',
  ).length;
  const fallbackExitCount = strategyTrades.filter(
    (trade) => trade.executionResolution === 'timeframe-fallback',
  ).length;
  const colorProV31Wins = colorProV31StrategyTrades.filter((trade) => trade.pnlPercent >= 0).length;
  const colorProV31RefinedExitCount = colorProV31StrategyTrades.filter(
    (trade) => trade.executionResolution === '1m-refined',
  ).length;
  const colorProV31FallbackExitCount = colorProV31StrategyTrades.filter(
    (trade) => trade.executionResolution === 'timeframe-fallback',
  ).length;
  const colorProV31OpenTrades = colorProV31StrategyActiveTrade ? 1 : 0;
  const openCode5mWins = openCode5mStrategyTrades.filter((trade) => trade.pnlPercent >= 0).length;
  const openCode5mOpenTrades = openCode5mStrategyActiveTrade ? 1 : 0;

  const showCompletedBotEvents = draftSettings.botTrades.visibility.showCompletedTrades;
  const showActiveBotEvents = draftSettings.botTrades.visibility.showActiveUpdates;

  const restoreDefaultsForTarget = () => {
    if (!openTarget) {
      return;
    }

    if (isBasicIndicatorKey(openTarget)) {
      const basicKey = asBasicIndicatorKey(openTarget);
      setDraftSettings((prev) => ({
        ...prev,
        basicStudies: {
          ...prev.basicStudies,
          [basicKey]: cloneSettings(defaultSettings.basicStudies[basicKey]),
        },
      }));
      return;
    }

    if (openTarget === 'movingAverage') {
      setDraftSettings((prev) => ({
        ...prev,
        movingAverage: cloneSettings(defaultSettings.movingAverage),
      }));
      return;
    }

    setDraftSettings((prev) => ({
      ...prev,
      [openTarget]: cloneSettings(defaultSettings[openTarget]),
    }));
  };

  const renderInputs = () => {
    if (openTarget === 'volume') {
      return <p className="text-sm text-slate-400">Volume has no input settings.</p>;
    }

    if (openTarget === 'rsi') {
      return (
        <div className="space-y-3">
          <SettingRow label="Length">
            <input
              type="number"
              min={2}
              max={100}
              step={1}
              value={draftSettings.rsi.inputs.length}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    inputs: {
                      ...prev.rsi.inputs,
                      length: Math.round(clamp(value, 2, 100)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'macd') {
      return (
        <div className="space-y-3">
          <SettingRow label="Fast Length">
            <input
              type="number"
              min={2}
              max={200}
              step={1}
              value={draftSettings.macd.inputs.fastLength}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => {
                  const fastLength = Math.round(clamp(value, 2, 200));
                  const slowLength = Math.max(prev.macd.inputs.slowLength, fastLength + 1);
                  return {
                    ...prev,
                    macd: {
                      ...prev.macd,
                      inputs: {
                        ...prev.macd.inputs,
                        fastLength,
                        slowLength,
                      },
                    },
                  };
                });
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Slow Length">
            <input
              type="number"
              min={3}
              max={300}
              step={1}
              value={draftSettings.macd.inputs.slowLength}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => {
                  const slowLength = Math.round(clamp(value, 3, 300));
                  const fastLength = Math.min(prev.macd.inputs.fastLength, slowLength - 1);
                  return {
                    ...prev,
                    macd: {
                      ...prev.macd,
                      inputs: {
                        ...prev.macd.inputs,
                        fastLength,
                        slowLength,
                      },
                    },
                  };
                });
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Signal Length">
            <input
              type="number"
              min={2}
              max={100}
              step={1}
              value={draftSettings.macd.inputs.signalLength}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    inputs: {
                      ...prev.macd.inputs,
                      signalLength: Math.round(clamp(value, 2, 100)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'cmVixFixUcsSqueeze') {
      return (
        <div className="space-y-3">
          <SettingRow label="Use Alternative Timeframe">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.inputs.useAlternativeTimeframe}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      useAlternativeTimeframe: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Alternative Timeframe">
            <select
              value={draftSettings.cmVixFixUcsSqueeze.inputs.alternativeTimeframe}
              onChange={(event) => {
                const nextTimeframe = event.target.value as CandleInterval;
                if (!MURREY_ALTERNATIVE_TIMEFRAME_OPTIONS.includes(nextTimeframe)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      alternativeTimeframe: nextTimeframe,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MURREY_ALTERNATIVE_TIMEFRAME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="LookBack Period Standard Deviation High">
            <input
              type="number"
              min={1}
              max={1000}
              step={1}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.pd}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      pd: Math.round(clamp(value, 1, 1000)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Bolinger Band Length">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.bbl}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      bbl: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Bollinger Band Standard Deviation Up">
            <input
              type="number"
              min={0.1}
              max={10}
              step={0.01}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.mult}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      mult: clamp(value, 0.1, 10),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Look Back Period Percentile High">
            <input
              type="number"
              min={1}
              max={1000}
              step={1}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.lb}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      lb: Math.round(clamp(value, 1, 1000)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Highest Percentile">
            <input
              type="number"
              min={0}
              max={2}
              step={0.01}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.ph}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      ph: clamp(value, 0, 2),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Lowest Percentile">
            <input
              type="number"
              min={0}
              max={2}
              step={0.01}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.pl}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      pl: clamp(value, 0, 2),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Show High Range - Based on Percentile and LookBack Period?">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.inputs.hp}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      hp: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Standard Deviation Line?">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.inputs.sd}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      sd: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Histogram Scale">
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.scale}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      scale: clamp(value, 0, 100),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Squeeze Length">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.length}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      length: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="BB MultFactor">
            <input
              type="number"
              min={0.1}
              max={10}
              step={0.01}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.multBB}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      multBB: clamp(value, 0.1, 10),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="KC MultFactor">
            <input
              type="number"
              min={0.1}
              max={10}
              step={0.01}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.multKC}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      multKC: clamp(value, 0.1, 10),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Momentum Smoothing">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.cmVixFixUcsSqueeze.inputs.smooth}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      smooth: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Use Bollinger Band Ratio">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.inputs.usebbr}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      usebbr: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Use Heikin Ashi Candle">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.inputs.useHAC}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      useHAC: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Use TrueRange (KC)">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.inputs.useTrueRange}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    inputs: {
                      ...prev.cmVixFixUcsSqueeze.inputs,
                      useTrueRange: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'movingAverage') {
      return (
        <div className="space-y-3">
          <SettingRow label="MA Type">
            <select
              value={draftSettings.movingAverage.inputs.maType}
              onChange={(event) => {
                const value = event.target.value as OverlaySettings['movingAverage']['inputs']['maType'];
                if (!MOVING_AVERAGE_TYPE_OPTIONS.includes(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    inputs: {
                      ...prev.movingAverage.inputs,
                      maType: value,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MOVING_AVERAGE_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option.toUpperCase()}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Source">
            <select
              value={draftSettings.movingAverage.inputs.source}
              onChange={(event) => {
                const value = event.target.value as OverlaySettings['movingAverage']['inputs']['source'];
                if (!MOVING_AVERAGE_SOURCE_OPTIONS.includes(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    inputs: {
                      ...prev.movingAverage.inputs,
                      source: value,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MOVING_AVERAGE_SOURCE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option[0].toUpperCase() + option.slice(1)}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Offset %">
            <input
              type="number"
              min={-100}
              max={100}
              step={0.1}
              value={draftSettings.movingAverage.inputs.offsetPercent}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    inputs: {
                      ...prev.movingAverage.inputs,
                      offsetPercent: clamp(value, -100, 100),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Period">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.movingAverage.inputs.period}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    inputs: {
                      ...prev.movingAverage.inputs,
                      period: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'murreyMathLinesV6') {
      return (
        <div className="space-y-3">
          <SettingRow label="Use Alternative Timeframe">
            <input
              type="checkbox"
              checked={draftSettings.murreyMathLinesV6.inputs.useAlternativeTimeframe}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      useAlternativeTimeframe: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Alternative Timeframe">
            <select
              value={draftSettings.murreyMathLinesV6.inputs.alternativeTimeframe}
              onChange={(event) => {
                const nextTimeframe = event.target.value as CandleInterval;
                if (!MURREY_ALTERNATIVE_TIMEFRAME_OPTIONS.includes(nextTimeframe)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      alternativeTimeframe: nextTimeframe,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MURREY_ALTERNATIVE_TIMEFRAME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Frame Size">
            <input
              type="number"
              min={8}
              max={256}
              step={4}
              value={draftSettings.murreyMathLinesV6.inputs.frameSize}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                const normalizedFrameSize = clamp(Math.round(value / 4) * 4, 8, 256);
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      frameSize: normalizedFrameSize,
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Frame Multiplier">
            <select
              value={draftSettings.murreyMathLinesV6.inputs.frameMultiplier}
              onChange={(event) => {
                const value = Number(event.target.value);
                const normalized = value === 1.5 || value === 2 ? value : 1;
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      frameMultiplier: normalized,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MURREY_FRAME_MULTIPLIER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}x
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Source Mode">
            <select
              value={draftSettings.murreyMathLinesV6.inputs.sourceMode}
              onChange={(event) => {
                const nextMode: 'highLow' | 'openClose' =
                  event.target.value === 'openClose' ? 'openClose' : 'highLow';
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      sourceMode: nextMode,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MURREY_SOURCE_MODE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value === 'highLow' ? 'High/Low' : 'Open/Close'}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Add Extended Lines">
            <input
              type="checkbox"
              checked={draftSettings.murreyMathLinesV6.inputs.addExtendedLines}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      addExtendedLines: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Extra Lines Count">
            <select
              value={draftSettings.murreyMathLinesV6.inputs.extraLinesCount}
              onChange={(event) => {
                const value = Number(event.target.value);
                const nextCount = value === 4 || value === 6 ? value : 2;
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      extraLinesCount: nextCount,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MURREY_EXTRA_LINES_COUNT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Lock To Session Start">
            <input
              type="checkbox"
              checked={draftSettings.murreyMathLinesV6.inputs.lockToSessionStart}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      lockToSessionStart: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Trading Session">
            <input
              type="text"
              value={draftSettings.murreyMathLinesV6.inputs.tradingSession}
              onChange={(event) => {
                const rawValue = event.target.value.toUpperCase();
                const normalizedValue = rawValue.replace(/[^0-9-]/g, '').slice(0, 9);
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    inputs: {
                      ...prev.murreyMathLinesV6.inputs,
                      tradingSession: normalizedValue,
                    },
                  },
                }));
              }}
              placeholder="0930-1600"
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'candleHelper') {
      return (
        <div className="space-y-3">
          <SettingRow label="Bullish Engulfing">
            <input
              type="checkbox"
              checked={draftSettings.candleHelper.inputs.detectBullishEngulfing}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    inputs: {
                      ...prev.candleHelper.inputs,
                      detectBullishEngulfing: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Bearish Engulfing">
            <input
              type="checkbox"
              checked={draftSettings.candleHelper.inputs.detectBearishEngulfing}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    inputs: {
                      ...prev.candleHelper.inputs,
                      detectBearishEngulfing: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Doji">
            <input
              type="checkbox"
              checked={draftSettings.candleHelper.inputs.detectDoji}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    inputs: {
                      ...prev.candleHelper.inputs,
                      detectDoji: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Doji Body %">
            <input
              type="number"
              min={1}
              max={40}
              step={1}
              value={draftSettings.candleHelper.inputs.dojiBodyRatioPercent}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    inputs: {
                      ...prev.candleHelper.inputs,
                      dojiBodyRatioPercent: Math.round(clamp(value, 1, 40)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'traderProV3') {
      return (
        <div className="space-y-3">
          <SettingRow label="Stop Loss Ratio">
            <input
              type="number"
              min={0.1}
              max={10}
              step={0.1}
              value={draftSettings.traderProV3.inputs.stopLossRatio}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    inputs: {
                      ...prev.traderProV3.inputs,
                      stopLossRatio: clamp(value, 0.1, 10),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'traderPro17') {
      return (
        <div className="space-y-3">
          <SettingRow label="Enable 200 DMA">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.inputs.enable200Dma}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    inputs: {
                      ...prev.traderPro17.inputs,
                      enable200Dma: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Enable Advanced Trading">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.inputs.enableAdvancedTrading}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    inputs: {
                      ...prev.traderPro17.inputs,
                      enableAdvancedTrading: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Enable Noob Protection">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.inputs.enableNoobProtection}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    inputs: {
                      ...prev.traderPro17.inputs,
                      enableNoobProtection: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Range Scale">
            <input
              type="number"
              min={0.5}
              max={1.5}
              step={0.01}
              value={draftSettings.traderPro17.inputs.rangeScale}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    inputs: {
                      ...prev.traderPro17.inputs,
                      rangeScale: clamp(value, 0.5, 1.5),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Use Alternative Timeframe">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.inputs.useAlternativeTimeframe}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    inputs: {
                      ...prev.traderPro17.inputs,
                      useAlternativeTimeframe: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Alternative Timeframe">
            <select
              value={draftSettings.traderPro17.inputs.alternativeTimeframe}
              onChange={(event) => {
                const nextTimeframe = event.target.value as CandleInterval;
                if (!MURREY_ALTERNATIVE_TIMEFRAME_OPTIONS.includes(nextTimeframe)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    inputs: {
                      ...prev.traderPro17.inputs,
                      alternativeTimeframe: nextTimeframe,
                    },
                  },
                }));
              }}
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {MURREY_ALTERNATIVE_TIMEFRAME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'bollingerBands') {
      return (
        <div className="space-y-3">
          <SettingRow label="Length">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.bollingerBands.inputs.length}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    inputs: {
                      ...prev.bollingerBands.inputs,
                      length: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="StdDev Multiplier">
            <input
              type="number"
              min={0.001}
              max={50}
              step={0.1}
              value={draftSettings.bollingerBands.inputs.multiplier}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    inputs: {
                      ...prev.bollingerBands.inputs,
                      multiplier: clamp(value, 0.001, 50),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Source">
            <select
              value={draftSettings.bollingerBands.inputs.source}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    inputs: {
                      ...prev.bollingerBands.inputs,
                      source: event.target.value as BollingerBandsSource,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {BOLLINGER_SOURCE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value.toUpperCase()}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Basis MA Type">
            <select
              value={draftSettings.bollingerBands.inputs.basisType}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    inputs: {
                      ...prev.bollingerBands.inputs,
                      basisType: event.target.value as BollingerBandsBasisType,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {BOLLINGER_BASIS_TYPE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value.toUpperCase()}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Offset">
            <input
              type="number"
              min={-500}
              max={500}
              step={1}
              value={draftSettings.bollingerBands.inputs.offset}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    inputs: {
                      ...prev.bollingerBands.inputs,
                      offset: Math.round(clamp(value, -500, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'ichimokuCloud') {
      return (
        <div className="space-y-3">
          <SettingRow label="Tenkan Period">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.ichimokuCloud.inputs.tenkanPeriod}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    inputs: {
                      ...prev.ichimokuCloud.inputs,
                      tenkanPeriod: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Kijun Period">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.ichimokuCloud.inputs.kijunPeriod}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    inputs: {
                      ...prev.ichimokuCloud.inputs,
                      kijunPeriod: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Senkou B Period">
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              value={draftSettings.ichimokuCloud.inputs.senkouBPeriod}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    inputs: {
                      ...prev.ichimokuCloud.inputs,
                      senkouBPeriod: Math.round(clamp(value, 1, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Displacement">
            <input
              type="number"
              min={0}
              max={500}
              step={1}
              value={draftSettings.ichimokuCloud.inputs.displacement}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    inputs: {
                      ...prev.ichimokuCloud.inputs,
                      displacement: Math.round(clamp(value, 0, 500)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <SettingRow label="RSI Length">
          <input
            type="number"
            min={2}
            max={100}
            step={1}
            value={colorProSettings.inputs.rsiLength}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (!Number.isFinite(value)) {
                return;
              }
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  rsiLength: Math.round(clamp(value, 2, 100)),
                },
              }));
            }}
            className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
          />
        </SettingRow>
        <SettingRow label="Stoch Length">
          <input
            type="number"
            min={2}
            max={100}
            step={1}
            value={colorProSettings.inputs.stochLength}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (!Number.isFinite(value)) {
                return;
              }
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  stochLength: Math.round(clamp(value, 2, 100)),
                },
              }));
            }}
            className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
          />
        </SettingRow>
        <SettingRow label="K Smoothing">
          <input
            type="number"
            min={1}
            max={20}
            step={1}
            value={colorProSettings.inputs.kSmoothing}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (!Number.isFinite(value)) {
                return;
              }
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  kSmoothing: Math.round(clamp(value, 1, 20)),
                },
              }));
            }}
            className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
          />
        </SettingRow>
        <SettingRow label="D Smoothing">
          <input
            type="number"
            min={1}
            max={20}
            step={1}
            value={colorProSettings.inputs.dSmoothing}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (!Number.isFinite(value)) {
                return;
              }
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  dSmoothing: Math.round(clamp(value, 1, 20)),
                },
              }));
            }}
            className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
          />
        </SettingRow>
        <SettingRow label="Bollinger Length">
          <input
            type="number"
            min={5}
            max={200}
            step={1}
            value={colorProSettings.inputs.bollingerLength}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (!Number.isFinite(value)) {
                return;
              }
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  bollingerLength: Math.round(clamp(value, 5, 200)),
                },
              }));
            }}
            className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
          />
        </SettingRow>
        <SettingRow label="Bollinger Multiplier">
          <input
            type="number"
            min={0.2}
            max={6}
            step={0.1}
            value={colorProSettings.inputs.bollingerMultiplier}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (!Number.isFinite(value)) {
                return;
              }
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  bollingerMultiplier: clamp(value, 0.2, 6),
                },
              }));
            }}
            className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
          />
        </SettingRow>
        <SettingRow label="Use Breakout Volume Filter">
          <input
            type="checkbox"
            checked={colorProSettings.inputs.useBreakoutVolumeFilter}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  useBreakoutVolumeFilter: event.target.checked,
                },
              }))
            }
            className="h-4 w-4 rounded border-slate-500 bg-slate-900"
          />
        </SettingRow>
        {openTarget === 'colorProTradingSuiteV31' && (
          <SettingRow label="Upper-Band Bridge Yellow">
            <input
              type="checkbox"
              checked={draftSettings.colorProTradingSuiteV31.inputs.allowUpperBandBridgeYellow}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  colorProTradingSuiteV31: {
                    ...prev.colorProTradingSuiteV31,
                    inputs: {
                      ...prev.colorProTradingSuiteV31.inputs,
                      allowUpperBandBridgeYellow: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        )}
        <SettingRow label="Breakout Vol Mult">
          <input
            type="number"
            min={0.5}
            max={5}
            step={0.05}
            value={colorProSettings.inputs.breakoutVolumeMultiplier}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (!Number.isFinite(value)) {
                return;
              }
              setColorProSettings((prev) => ({
                ...prev,
                inputs: {
                  ...prev.inputs,
                  breakoutVolumeMultiplier: clamp(value, 0.5, 5),
                },
              }));
            }}
            className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
          />
        </SettingRow>
      </div>
    );
  };

  const renderStyle = () => {
    if (openTarget === 'volume') {
      return (
        <div className="space-y-3">
          <SettingRow label="Up Color">
            <input
              type="color"
              value={draftSettings.volume.style.upColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  volume: {
                    ...prev.volume,
                    style: {
                      ...prev.volume.style,
                      upColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Down Color">
            <input
              type="color"
              value={draftSettings.volume.style.downColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  volume: {
                    ...prev.volume,
                    style: {
                      ...prev.volume.style,
                      downColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Opacity">
            <input
              type="range"
              min={0.1}
              max={1}
              step={0.05}
              value={draftSettings.volume.style.opacity}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  volume: {
                    ...prev.volume,
                    style: {
                      ...prev.volume.style,
                      opacity: clamp(value, 0.1, 1),
                    },
                  },
                }));
              }}
              className="w-28"
            />
            <span className="w-10 text-right text-xs text-slate-300">
              {draftSettings.volume.style.opacity.toFixed(2)}
            </span>
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'rsi') {
      return (
        <div className="space-y-3">
          <SettingRow label="Line Color">
            <input
              type="color"
              value={draftSettings.rsi.style.lineColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    style: {
                      ...prev.rsi.style,
                      lineColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Overbought Color">
            <input
              type="color"
              value={draftSettings.rsi.style.overboughtColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    style: {
                      ...prev.rsi.style,
                      overboughtColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Oversold Color">
            <input
              type="color"
              value={draftSettings.rsi.style.oversoldColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    style: {
                      ...prev.rsi.style,
                      oversoldColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Line Width">
            <select
              value={draftSettings.rsi.style.lineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    style: {
                      ...prev.rsi.style,
                      lineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'macd') {
      return (
        <div className="space-y-3">
          <SettingRow label="MACD Color">
            <input
              type="color"
              value={draftSettings.macd.style.macdColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    style: {
                      ...prev.macd.style,
                      macdColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Signal Color">
            <input
              type="color"
              value={draftSettings.macd.style.signalColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    style: {
                      ...prev.macd.style,
                      signalColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Histogram + Color">
            <input
              type="color"
              value={draftSettings.macd.style.histogramPositiveColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    style: {
                      ...prev.macd.style,
                      histogramPositiveColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Histogram - Color">
            <input
              type="color"
              value={draftSettings.macd.style.histogramNegativeColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    style: {
                      ...prev.macd.style,
                      histogramNegativeColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Line Width">
            <select
              value={draftSettings.macd.style.lineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    style: {
                      ...prev.macd.style,
                      lineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'cmVixFixUcsSqueeze') {
      return (
        <div className="space-y-3">
          <SettingRow label="WVF Spike Color">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.wvfSpikeColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      wvfSpikeColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="WVF Base Color">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.wvfBaseColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      wvfBaseColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Momentum + Rising">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.momentumPositiveRisingColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      momentumPositiveRisingColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Momentum + Falling">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.momentumPositiveFallingColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      momentumPositiveFallingColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Momentum - Falling">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.momentumNegativeFallingColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      momentumNegativeFallingColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Momentum - Rising">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.momentumNegativeRisingColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      momentumNegativeRisingColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Squeeze On Color">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.squeezeOnColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      squeezeOnColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Squeeze Off Color">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.squeezeOffColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      squeezeOffColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="No Squeeze Color">
            <input
              type="color"
              value={draftSettings.cmVixFixUcsSqueeze.style.noSqueezeColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    style: {
                      ...prev.cmVixFixUcsSqueeze.style,
                      noSqueezeColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'movingAverage') {
      return (
        <div className="space-y-3">
          <SettingRow label="Line Color">
            <input
              type="color"
              value={draftSettings.movingAverage.style.color}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    style: {
                      ...prev.movingAverage.style,
                      color: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Line Width">
            <select
              value={draftSettings.movingAverage.style.lineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    style: {
                      ...prev.movingAverage.style,
                      lineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Line Style">
            <select
              value={draftSettings.movingAverage.style.lineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    style: {
                      ...prev.movingAverage.style,
                      lineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'candleHelper') {
      return (
        <div className="space-y-3">
          <SettingRow label="Bullish Color">
            <input
              type="color"
              value={draftSettings.candleHelper.style.bullishEngulfingColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    style: {
                      ...prev.candleHelper.style,
                      bullishEngulfingColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Bearish Color">
            <input
              type="color"
              value={draftSettings.candleHelper.style.bearishEngulfingColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    style: {
                      ...prev.candleHelper.style,
                      bearishEngulfingColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Doji Color">
            <input
              type="color"
              value={draftSettings.candleHelper.style.dojiColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    style: {
                      ...prev.candleHelper.style,
                      dojiColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Marker Size">
            <select
              value={draftSettings.candleHelper.style.markerSize}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    style: {
                      ...prev.candleHelper.style,
                      markerSize: Math.round(clamp(Number(event.target.value), 1, 4)),
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'traderProV3') {
      return (
        <div className="space-y-3">
          <SettingRow label="Entry Color">
            <input
              type="color"
              value={draftSettings.traderProV3.style.entryColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      entryColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="TP1 Color">
            <input
              type="color"
              value={draftSettings.traderProV3.style.tp1Color}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      tp1Color: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="TP2 Color">
            <input
              type="color"
              value={draftSettings.traderProV3.style.tp2Color}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      tp2Color: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Stop Color">
            <input
              type="color"
              value={draftSettings.traderProV3.style.stopColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      stopColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Resistance Color">
            <input
              type="color"
              value={draftSettings.traderProV3.style.resistanceColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      resistanceColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Midpoint Color">
            <input
              type="color"
              value={draftSettings.traderProV3.style.midpointColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      midpointColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Support Color">
            <input
              type="color"
              value={draftSettings.traderProV3.style.supportColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      supportColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Trade Line Width">
            <select
              value={draftSettings.traderProV3.style.tradeLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      tradeLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Murrey Line Width">
            <select
              value={draftSettings.traderProV3.style.murreyLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      murreyLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Murrey Line Style">
            <select
              value={draftSettings.traderProV3.style.murreyLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    style: {
                      ...prev.traderProV3.style,
                      murreyLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'traderPro17') {
      return (
        <div className="space-y-3">
          <SettingRow label="Resistance Color">
            <input
              type="color"
              value={draftSettings.traderPro17.style.resistanceColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    style: {
                      ...prev.traderPro17.style,
                      resistanceColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Neutral Color">
            <input
              type="color"
              value={draftSettings.traderPro17.style.neutralColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    style: {
                      ...prev.traderPro17.style,
                      neutralColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Support Color">
            <input
              type="color"
              value={draftSettings.traderPro17.style.supportColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    style: {
                      ...prev.traderPro17.style,
                      supportColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Extension Color">
            <input
              type="color"
              value={draftSettings.traderPro17.style.extensionColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    style: {
                      ...prev.traderPro17.style,
                      extensionColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Pullback Text Color">
            <input
              type="color"
              value={draftSettings.traderPro17.style.pullbackTextColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    style: {
                      ...prev.traderPro17.style,
                      pullbackTextColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Algo Target Color">
            <input
              type="color"
              value={draftSettings.traderPro17.style.algoTargetColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    style: {
                      ...prev.traderPro17.style,
                      algoTargetColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'bollingerBands') {
      return (
        <div className="space-y-3">
          <SettingRow label="Upper Color">
            <input
              type="color"
              value={draftSettings.bollingerBands.style.upperColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      upperColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Basis Color">
            <input
              type="color"
              value={draftSettings.bollingerBands.style.basisColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      basisColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Lower Color">
            <input
              type="color"
              value={draftSettings.bollingerBands.style.lowerColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      lowerColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Upper Width">
            <select
              value={draftSettings.bollingerBands.style.upperLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      upperLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Upper Style">
            <select
              value={draftSettings.bollingerBands.style.upperLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      upperLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Basis Width">
            <select
              value={draftSettings.bollingerBands.style.basisLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      basisLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Basis Style">
            <select
              value={draftSettings.bollingerBands.style.basisLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      basisLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Lower Width">
            <select
              value={draftSettings.bollingerBands.style.lowerLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      lowerLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Lower Style">
            <select
              value={draftSettings.bollingerBands.style.lowerLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      lowerLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Fill Color">
            <input
              type="color"
              value={draftSettings.bollingerBands.style.fillColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      fillColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Fill Opacity">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={draftSettings.bollingerBands.style.fillOpacity}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    style: {
                      ...prev.bollingerBands.style,
                      fillOpacity: Math.round(clamp(value, 0, 100)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'ichimokuCloud') {
      return (
        <div className="space-y-3">
          <SettingRow label="Tenkan Color">
            <input
              type="color"
              value={draftSettings.ichimokuCloud.style.tenkanColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      tenkanColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Kijun Color">
            <input
              type="color"
              value={draftSettings.ichimokuCloud.style.kijunColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      kijunColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Senkou A Color">
            <input
              type="color"
              value={draftSettings.ichimokuCloud.style.senkouAColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      senkouAColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Senkou B Color">
            <input
              type="color"
              value={draftSettings.ichimokuCloud.style.senkouBColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      senkouBColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Chikou Color">
            <input
              type="color"
              value={draftSettings.ichimokuCloud.style.chikouColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      chikouColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Bullish Fill">
            <input
              type="color"
              value={draftSettings.ichimokuCloud.style.bullishFillColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      bullishFillColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Bearish Fill">
            <input
              type="color"
              value={draftSettings.ichimokuCloud.style.bearishFillColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      bearishFillColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Tenkan Width">
            <select
              value={draftSettings.ichimokuCloud.style.tenkanLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      tenkanLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Tenkan Style">
            <select
              value={draftSettings.ichimokuCloud.style.tenkanLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      tenkanLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Kijun Width">
            <select
              value={draftSettings.ichimokuCloud.style.kijunLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      kijunLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Kijun Style">
            <select
              value={draftSettings.ichimokuCloud.style.kijunLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      kijunLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Senkou A Width">
            <select
              value={draftSettings.ichimokuCloud.style.senkouALineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      senkouALineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Senkou A Style">
            <select
              value={draftSettings.ichimokuCloud.style.senkouALineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      senkouALineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Senkou B Width">
            <select
              value={draftSettings.ichimokuCloud.style.senkouBLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      senkouBLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Senkou B Style">
            <select
              value={draftSettings.ichimokuCloud.style.senkouBLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      senkouBLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Chikou Width">
            <select
              value={draftSettings.ichimokuCloud.style.chikouLineWidth}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      chikouLineWidth: Number(event.target.value) as 1 | 2 | 3 | 4,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </SettingRow>
          <SettingRow label="Chikou Style">
            <select
              value={draftSettings.ichimokuCloud.style.chikouLineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      chikouLineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Cloud Opacity">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={draftSettings.ichimokuCloud.style.fillOpacity}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    style: {
                      ...prev.ichimokuCloud.style,
                      fillOpacity: Math.round(clamp(value, 0, 100)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'murreyMathLinesV6') {
      return (
        <div className="space-y-3">
          <SettingRow label="Major Line Color">
            <input
              type="color"
              value={draftSettings.murreyMathLinesV6.style.majorLineColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      majorLineColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Range Line Color">
            <input
              type="color"
              value={draftSettings.murreyMathLinesV6.style.rangeLineColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      rangeLineColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Pivot Line Color">
            <input
              type="color"
              value={draftSettings.murreyMathLinesV6.style.pivotLineColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      pivotLineColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Weak Line Color">
            <input
              type="color"
              value={draftSettings.murreyMathLinesV6.style.weakLineColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      weakLineColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Extended Line Color">
            <input
              type="color"
              value={draftSettings.murreyMathLinesV6.style.extendedLineColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      extendedLineColor: event.target.value,
                    },
                  },
                }))
              }
              className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
            />
          </SettingRow>
          <SettingRow label="Line Width">
            <input
              type="number"
              min={1}
              max={4}
              step={1}
              value={draftSettings.murreyMathLinesV6.style.lineWidth}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      lineWidth: Math.round(clamp(value, 1, 4)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Line Style">
            <select
              value={draftSettings.murreyMathLinesV6.style.lineStyle}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      lineStyle: event.target.value as OverlayLineStyle,
                    },
                  },
                }))
              }
              className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100"
            >
              {LINE_STYLE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </SettingRow>
          <SettingRow label="Zone Opacity">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={draftSettings.murreyMathLinesV6.style.zoneOpacity}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    style: {
                      ...prev.murreyMathLinesV6.style,
                      zoneOpacity: Math.round(clamp(value, 0, 100)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <SettingRow label="Bollinger Color">
          <input
            type="color"
            value={colorProSettings.style.bollingerColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  bollingerColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Basis Color">
          <input
            type="color"
            value={colorProSettings.style.basisColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  basisColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Daily Resistance">
          <input
            type="color"
            value={colorProSettings.style.dailyResistanceColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  dailyResistanceColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Daily Support">
          <input
            type="color"
            value={colorProSettings.style.dailySupportColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  dailySupportColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Weekly Resistance">
          <input
            type="color"
            value={colorProSettings.style.weeklyResistanceColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  weeklyResistanceColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Weekly Support">
          <input
            type="color"
            value={colorProSettings.style.weeklySupportColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  weeklySupportColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Pivot Color">
          <input
            type="color"
            value={colorProSettings.style.pivotColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  pivotColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Bearish Pivot Signal">
          <input
            type="color"
            value={colorProSettings.style.bearishPivotSignalColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  bearishPivotSignalColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Confirmed Reversal">
          <input
            type="color"
            value={colorProSettings.style.confirmedReversalSignalColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  confirmedReversalSignalColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Bullish R Signal">
          <input
            type="color"
            value={colorProSettings.style.bullishRegularSignalColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  bullishRegularSignalColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Bearish R Signal">
          <input
            type="color"
            value={colorProSettings.style.bearishRegularSignalColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  bearishRegularSignalColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Bullish H Signal">
          <input
            type="color"
            value={colorProSettings.style.bullishHiddenSignalColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  bullishHiddenSignalColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
        <SettingRow label="Bearish H Signal">
          <input
            type="color"
            value={colorProSettings.style.bearishHiddenSignalColor}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                style: {
                  ...prev.style,
                  bearishHiddenSignalColor: event.target.value,
                },
              }))
            }
            className="h-8 w-12 rounded border border-slate-600 bg-slate-900 p-0.5"
          />
        </SettingRow>
      </div>
    );
  };

  const renderVisibility = () => {
    if (openTarget === 'volume') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show Histogram">
            <input
              type="checkbox"
              checked={draftSettings.volume.visibility.showHistogram}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  volume: {
                    ...prev.volume,
                    visibility: {
                      ...prev.volume.visibility,
                      showHistogram: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'rsi') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show RSI Line">
            <input
              type="checkbox"
              checked={draftSettings.rsi.visibility.showLine}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    visibility: {
                      ...prev.rsi.visibility,
                      showLine: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show 30/70 Zones">
            <input
              type="checkbox"
              checked={draftSettings.rsi.visibility.showZones}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    visibility: {
                      ...prev.rsi.visibility,
                      showZones: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'macd') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show MACD Line">
            <input
              type="checkbox"
              checked={draftSettings.macd.visibility.showMacdLine}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    visibility: {
                      ...prev.macd.visibility,
                      showMacdLine: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Signal Line">
            <input
              type="checkbox"
              checked={draftSettings.macd.visibility.showSignalLine}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    visibility: {
                      ...prev.macd.visibility,
                      showSignalLine: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Histogram">
            <input
              type="checkbox"
              checked={draftSettings.macd.visibility.showHistogram}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    visibility: {
                      ...prev.macd.visibility,
                      showHistogram: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'cmVixFixUcsSqueeze') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show WVF Histogram">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.visibility.showWvfHistogram}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    visibility: {
                      ...prev.cmVixFixUcsSqueeze.visibility,
                      showWvfHistogram: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Momentum Histogram">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.visibility.showMomentumHistogram}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    visibility: {
                      ...prev.cmVixFixUcsSqueeze.visibility,
                      showMomentumHistogram: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Squeeze State Line">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.visibility.showSqueezeStateLine}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    visibility: {
                      ...prev.cmVixFixUcsSqueeze.visibility,
                      showSqueezeStateLine: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Indicator Labels">
            <input
              type="checkbox"
              checked={draftSettings.cmVixFixUcsSqueeze.visibility.showSeriesLabels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  cmVixFixUcsSqueeze: {
                    ...prev.cmVixFixUcsSqueeze,
                    visibility: {
                      ...prev.cmVixFixUcsSqueeze.visibility,
                      showSeriesLabels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Indicator Labels">
            <input
              type="checkbox"
              checked={draftSettings.rsi.visibility.showSeriesLabels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  rsi: {
                    ...prev.rsi,
                    visibility: {
                      ...prev.rsi.visibility,
                      showSeriesLabels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Indicator Labels">
            <input
              type="checkbox"
              checked={draftSettings.macd.visibility.showSeriesLabels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  macd: {
                    ...prev.macd,
                    visibility: {
                      ...prev.macd.visibility,
                      showSeriesLabels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'movingAverage') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show MA Line">
            <input
              type="checkbox"
              checked={draftSettings.movingAverage.visibility.showLine}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  movingAverage: {
                    ...prev.movingAverage,
                    visibility: {
                      ...prev.movingAverage.visibility,
                      showLine: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'candleHelper') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show Markers">
            <input
              type="checkbox"
              checked={draftSettings.candleHelper.visibility.showMarkers}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    visibility: {
                      ...prev.candleHelper.visibility,
                      showMarkers: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Labels">
            <input
              type="checkbox"
              checked={draftSettings.candleHelper.visibility.showLabels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  candleHelper: {
                    ...prev.candleHelper,
                    visibility: {
                      ...prev.candleHelper.visibility,
                      showLabels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'traderProV3') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show Trade Levels">
            <input
              type="checkbox"
              checked={draftSettings.traderProV3.visibility.showTradeLevels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    visibility: {
                      ...prev.traderProV3.visibility,
                      showTradeLevels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Murrey Levels">
            <input
              type="checkbox"
              checked={draftSettings.traderProV3.visibility.showMurreyLevels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    visibility: {
                      ...prev.traderProV3.visibility,
                      showMurreyLevels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Info Badge">
            <input
              type="checkbox"
              checked={draftSettings.traderProV3.visibility.showInfoBadge}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    visibility: {
                      ...prev.traderProV3.visibility,
                      showInfoBadge: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'traderPro17') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show Level Guides">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.visibility.showLevelGuides}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    visibility: {
                      ...prev.traderPro17.visibility,
                      showLevelGuides: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Pullback Hints">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.visibility.showPullbackHints}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    visibility: {
                      ...prev.traderPro17.visibility,
                      showPullbackHints: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Alignment Hints">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.visibility.showAlignmentHints}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    visibility: {
                      ...prev.traderPro17.visibility,
                      showAlignmentHints: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Algo Targets">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.visibility.showAlgoTargets}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    visibility: {
                      ...prev.traderPro17.visibility,
                      showAlgoTargets: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Horizontal Offset (bars)">
            <input
              type="number"
              min={-50}
              max={50}
              step={1}
              value={draftSettings.traderPro17.visibility.horizontalOffset}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (!Number.isFinite(value)) {
                  return;
                }
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    visibility: {
                      ...prev.traderPro17.visibility,
                      horizontalOffset: Math.round(clamp(value, -50, 50)),
                    },
                  },
                }));
              }}
              className="w-24 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-right text-slate-100"
            />
          </SettingRow>
          <SettingRow label="Show Info Badge">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17.visibility.showInfoBadge}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17: {
                    ...prev.traderPro17,
                    visibility: {
                      ...prev.traderPro17.visibility,
                      showInfoBadge: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'bollingerBands') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show Upper Band">
            <input
              type="checkbox"
              checked={draftSettings.bollingerBands.visibility.showUpper}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    visibility: {
                      ...prev.bollingerBands.visibility,
                      showUpper: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Basis">
            <input
              type="checkbox"
              checked={draftSettings.bollingerBands.visibility.showBasis}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    visibility: {
                      ...prev.bollingerBands.visibility,
                      showBasis: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Lower Band">
            <input
              type="checkbox"
              checked={draftSettings.bollingerBands.visibility.showLower}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    visibility: {
                      ...prev.bollingerBands.visibility,
                      showLower: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Fill">
            <input
              type="checkbox"
              checked={draftSettings.bollingerBands.visibility.showFill}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  bollingerBands: {
                    ...prev.bollingerBands,
                    visibility: {
                      ...prev.bollingerBands.visibility,
                      showFill: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'ichimokuCloud') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show Tenkan">
            <input
              type="checkbox"
              checked={draftSettings.ichimokuCloud.visibility.showTenkan}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    visibility: {
                      ...prev.ichimokuCloud.visibility,
                      showTenkan: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Kijun">
            <input
              type="checkbox"
              checked={draftSettings.ichimokuCloud.visibility.showKijun}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    visibility: {
                      ...prev.ichimokuCloud.visibility,
                      showKijun: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Senkou A">
            <input
              type="checkbox"
              checked={draftSettings.ichimokuCloud.visibility.showSenkouA}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    visibility: {
                      ...prev.ichimokuCloud.visibility,
                      showSenkouA: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Senkou B">
            <input
              type="checkbox"
              checked={draftSettings.ichimokuCloud.visibility.showSenkouB}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    visibility: {
                      ...prev.ichimokuCloud.visibility,
                      showSenkouB: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Chikou">
            <input
              type="checkbox"
              checked={draftSettings.ichimokuCloud.visibility.showChikou}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    visibility: {
                      ...prev.ichimokuCloud.visibility,
                      showChikou: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Cloud">
            <input
              type="checkbox"
              checked={draftSettings.ichimokuCloud.visibility.showCloud}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  ichimokuCloud: {
                    ...prev.ichimokuCloud,
                    visibility: {
                      ...prev.ichimokuCloud.visibility,
                      showCloud: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    if (openTarget === 'murreyMathLinesV6') {
      return (
        <div className="space-y-3">
          <SettingRow label="Show Murrey Lines">
            <input
              type="checkbox"
              checked={draftSettings.murreyMathLinesV6.visibility.showLines}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    visibility: {
                      ...prev.murreyMathLinesV6.visibility,
                      showLines: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Premium/Discount Zones">
            <input
              type="checkbox"
              checked={draftSettings.murreyMathLinesV6.visibility.showZones}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    visibility: {
                      ...prev.murreyMathLinesV6.visibility,
                      showZones: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Info Badge">
            <input
              type="checkbox"
              checked={draftSettings.murreyMathLinesV6.visibility.showInfo}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    visibility: {
                      ...prev.murreyMathLinesV6.visibility,
                      showInfo: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Indicator Labels">
            <input
              type="checkbox"
              checked={draftSettings.murreyMathLinesV6.visibility.showSeriesLabels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  murreyMathLinesV6: {
                    ...prev.murreyMathLinesV6,
                    visibility: {
                      ...prev.murreyMathLinesV6.visibility,
                      showSeriesLabels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Indicator Labels">
            <input
              type="checkbox"
              checked={draftSettings.traderProV3.visibility.showSeriesLabels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderProV3: {
                    ...prev.traderProV3,
                    visibility: {
                      ...prev.traderProV3.visibility,
                      showSeriesLabels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
          <SettingRow label="Show Indicator Labels">
            <input
              type="checkbox"
              checked={draftSettings.traderPro17Strategy.visibility.showSeriesLabels}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  traderPro17Strategy: {
                    ...prev.traderPro17Strategy,
                    visibility: {
                      ...prev.traderPro17Strategy.visibility,
                      showSeriesLabels: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <SettingRow label="Show Bollinger">
          <input
            type="checkbox"
            checked={colorProSettings.visibility.showBollinger}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  showBollinger: event.target.checked,
                },
              }))
            }
            className="h-4 w-4 rounded border-slate-500 bg-slate-900"
          />
        </SettingRow>
        <SettingRow label="Show Daily Levels">
          <input
            type="checkbox"
            checked={colorProSettings.visibility.showDailyLevels}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  showDailyLevels: event.target.checked,
                },
              }))
            }
            className="h-4 w-4 rounded border-slate-500 bg-slate-900"
          />
        </SettingRow>
        <SettingRow label="Show Weekly Levels">
          <input
            type="checkbox"
            checked={colorProSettings.visibility.showWeeklyLevels}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  showWeeklyLevels: event.target.checked,
                },
              }))
            }
            className="h-4 w-4 rounded border-slate-500 bg-slate-900"
          />
        </SettingRow>
        <SettingRow label="Show Signals">
          <input
            type="checkbox"
            checked={colorProSettings.visibility.showSignals}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  showSignals: event.target.checked,
                },
              }))
            }
            className="h-4 w-4 rounded border-slate-500 bg-slate-900"
          />
        </SettingRow>
        <SettingRow label="Color Candles">
          <input
            type="checkbox"
            checked={colorProSettings.visibility.showCandleColors}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  showCandleColors: event.target.checked,
                },
              }))
            }
            className="h-4 w-4 rounded border-slate-500 bg-slate-900"
          />
        </SettingRow>
        {openTarget === 'colorProTradingSuiteV31' && (
          <SettingRow label="Fill Entire Candle">
            <input
              type="checkbox"
              checked={draftSettings.colorProTradingSuiteV31.visibility.matchEntireCandleToColor}
              onChange={(event) =>
                setDraftSettings((prev) => ({
                  ...prev,
                  colorProTradingSuiteV31: {
                    ...prev.colorProTradingSuiteV31,
                    visibility: {
                      ...prev.colorProTradingSuiteV31.visibility,
                      matchEntireCandleToColor: event.target.checked,
                    },
                  },
                }))
              }
              className="h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </SettingRow>
        )}
        <SettingRow label="Show Info Badge">
          <input
            type="checkbox"
            checked={colorProSettings.visibility.showInfoBadge}
            onChange={(event) =>
              setColorProSettings((prev) => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  showInfoBadge: event.target.checked,
                },
              }))
            }
            className="h-4 w-4 rounded border-slate-500 bg-slate-900"
          />
        </SettingRow>
      </div>
    );
  };

  const strategySettingsContent =
    openTarget === 'traderPro17Strategy' || openTarget === 'openCode5mStrategy' ? (
      <Suspense fallback={null}>
        <StrategySettingsPanels
          openTarget={openTarget}
          activeTab={activeTab}
          draftSettings={draftSettings}
          setDraftSettings={setDraftSettings}
          strategyRefreshDateErrorMessage={strategyRefreshDateErrorMessage}
          strategyRefreshDateInfoMessage={strategyRefreshDateInfoMessage}
        />
      </Suspense>
    ) : null;

  const basicIndicatorSettingsContent =
    isBasicIndicatorKey(openTarget) ? (
      <Suspense fallback={null}>
        <BasicIndicatorSettingsPanels
          openTarget={asBasicIndicatorKey(openTarget)}
          activeTab={activeTab}
          draftSettings={draftSettings}
          setDraftSettings={setDraftSettings}
        />
      </Suspense>
    ) : null;

  const colorProV31StrategySettingsContent =
    openTarget === 'colorProTradingSuiteV31Strategy' ? (
      <Suspense fallback={null}>
        <ColorProV31StrategySettingsPanels
          activeTab={activeTab}
          draftSettings={draftSettings}
          setDraftSettings={setDraftSettings}
        />
      </Suspense>
    ) : null;

  const botTradesSettingsContent =
    openTarget === 'botTrades' ? (
      <Suspense fallback={null}>
        <BotTradesSettingsPanels activeTab={activeTab} draftSettings={draftSettings} setDraftSettings={setDraftSettings} />
      </Suspense>
    ) : null;

  const content =
    basicIndicatorSettingsContent ??
    colorProV31StrategySettingsContent ??
    botTradesSettingsContent ??
    strategySettingsContent ??
    (activeTab === 'inputs' ? renderInputs() : activeTab === 'style' ? renderStyle() : renderVisibility());
  const sidePanels = hasSidePanel ? (
    <Suspense fallback={null}>
      <OverlaySettingsSidePanels
        isStrategyTarget={isStrategyTarget}
        strategyTimeframe={strategyTimeframe}
        strategyRefreshWindowLabel={strategyRefreshWindowLabel}
        strategyRefreshNeedsManualApply={strategyRefreshNeedsManualApply}
        onRefreshStrategyData={onRefreshStrategyData}
        isRefreshingStrategyData={isRefreshingStrategyData}
        canRefreshStrategyData={canRefreshStrategyData}
        onExportStrategy={onExportStrategy}
        canExportStrategy={canExportStrategy}
        isStrategyAdded={isStrategyAdded}
        isStrategyTimeframeSelected={isStrategyTimeframeSelected}
        strategyStats={strategyStats}
        isStrategyMinuteRefinementEnabled={isStrategyMinuteRefinementEnabled}
        isStrategyMinuteSnapshotLoading={isStrategyMinuteSnapshotLoading}
        strategyMinuteDataErrorMessage={strategyMinuteDataErrorMessage}
        isStrategyMinuteRefinementLoading={isStrategyMinuteRefinementLoading}
        isStrategyMinuteRetryScheduled={isStrategyMinuteRetryScheduled}
        isStrategyMinuteCoverageStalled={isStrategyMinuteCoverageStalled}
        isStrategyMinuteRefinementReady={isStrategyMinuteRefinementReady}
        missingStrategyMinuteBuckets={missingStrategyMinuteBuckets}
        strategyTrades={strategyTrades}
        trailingWins={trailingWins}
        trailingLosses={trailingLosses}
        refinedExitCount={refinedExitCount}
        fallbackExitCount={fallbackExitCount}
        isColorProV31StrategyTarget={isColorProV31StrategyTarget}
        colorProV31StrategyTimeframe={colorProV31StrategyTimeframe}
        colorProV31RefreshWindowLabel={colorProV31RefreshWindowLabel}
        onRefreshColorProV31StrategyData={onRefreshColorProV31StrategyData}
        isRefreshingColorProV31StrategyData={isRefreshingColorProV31StrategyData}
        canRefreshColorProV31StrategyData={canRefreshColorProV31StrategyData}
        onExportColorProV31Strategy={onExportColorProV31Strategy}
        canExportColorProV31Strategy={canExportColorProV31Strategy}
        isColorProV31StrategyAdded={isColorProV31StrategyAdded}
        isColorProV31StrategyTimeframeSelected={isColorProV31StrategyTimeframeSelected}
        colorProV31StrategyStats={colorProV31StrategyStats}
        colorProV31StrategyTrades={colorProV31StrategyTrades}
        colorProV31StrategyActiveTrade={colorProV31StrategyActiveTrade}
        colorProV31Wins={colorProV31Wins}
        colorProV31RefinedExitCount={colorProV31RefinedExitCount}
        colorProV31FallbackExitCount={colorProV31FallbackExitCount}
        colorProV31OpenTrades={colorProV31OpenTrades}
        isOpenCode5mStrategyTarget={isOpenCode5mStrategyTarget}
        openCode5mStrategyTimeframe={openCode5mStrategyTimeframe}
        isOpenCode5mStrategyAdded={isOpenCode5mStrategyAdded}
        isOpenCode5mStrategyTimeframeSelected={isOpenCode5mStrategyTimeframeSelected}
        openCode5mStrategyStats={openCode5mStrategyStats}
        openCode5mStrategyTrades={openCode5mStrategyTrades}
        openCode5mStrategyActiveTrade={openCode5mStrategyActiveTrade}
        openCode5mWins={openCode5mWins}
        openCode5mOpenTrades={openCode5mOpenTrades}
        isBotTradesTarget={isBotTradesTarget}
        currentSymbol={currentSymbol}
        botTradeEvents={botTradeEvents}
        showCompletedBotEvents={showCompletedBotEvents}
        showActiveBotEvents={showActiveBotEvents}
        isResettingBotSession={isResettingBotSession}
        isClearingBotHistory={isClearingBotHistory}
        botHistoryMessage={botHistoryMessage}
        botHistoryError={botHistoryError}
        onRequestResetBotSession={() => setBotConfirmAction('resetSession')}
        onRequestClearHistory={() => setBotConfirmAction('clearHistory')}
      />
    </Suspense>
  ) : null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 flex bg-transparent ${
          isKeyboardSafeLayout ? 'items-start justify-stretch p-3 sm:p-4' : 'items-center justify-center p-4'
        }`}
        style={viewportStyle}
        onClick={onClose}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className={`w-full border border-slate-700 bg-slate-900 shadow-2xl ${
            isKeyboardSafeLayout
              ? 'flex max-h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl'
              : hasSidePanel
                ? 'max-w-6xl rounded-xl'
                : 'max-w-md rounded-xl'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
            <h2 className="text-xl font-semibold text-slate-100">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
              aria-label="Close settings"
            >
              <CloseIcon />
            </button>
          </div>

          <div className={`flex min-h-0 flex-1 flex-col ${hasSidePanel ? 'lg:flex-row' : ''}`}>
            <div className="min-w-0 flex-1 px-4 pt-2">
              <div className="flex items-center gap-5 border-b border-slate-700 text-sm">
                {TABS.map((tab) => {
                  const selected = tab === activeTab;
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`border-b-2 pb-2 font-semibold capitalize transition-colors ${
                        selected
                          ? 'border-slate-200 text-slate-100'
                          : 'border-transparent text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>

              <div className={`space-y-3 overflow-y-auto py-4 ${isKeyboardSafeLayout ? 'min-h-0' : 'max-h-[55vh]'}`}>
                {content}
              </div>
            </div>

            {sidePanels}
          </div>

          <div className="flex items-center justify-between border-t border-slate-700 px-4 py-3">
            <button
              type="button"
              onClick={restoreDefaultsForTarget}
              className="rounded-md border border-slate-600 bg-slate-900 px-3 py-1 text-sm text-slate-200 transition-colors hover:bg-slate-800"
            >
              Defaults
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  onApply(initialSettings);
                  onClose();
                }}
                className="rounded-md border border-slate-500 px-4 py-1.5 text-sm text-slate-200 transition-colors hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onApply(draftSettings);
                  onClose();
                }}
                className="rounded-md bg-slate-100 px-4 py-1.5 text-sm font-medium text-slate-900 transition-colors hover:bg-white"
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={botConfirmConfig !== null}
        title={botConfirmConfig?.title ?? ''}
        message={botConfirmConfig?.message ?? ''}
        confirmLabel={botConfirmConfig?.confirmLabel ?? 'Yes'}
        confirmTone={botConfirmConfig?.confirmTone ?? 'primary'}
        onClose={handleCloseBotConfirm}
        onConfirm={handleConfirmBotAction}
      />
    </>
  );
}
